<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'

import {
  $getWaitingRoomDuplicateComparison,
  $getWaitingRoomDuplicateComparisonWaitingRoomFiles,
  type WaitingRoomDuplicateComparisonFile,
  type WaitingRoomDuplicateComparisonResponse,
  type WaitingRoomFile,
} from '@/api/fileManagement'
import ProjectFilesWaitingRoomDuplicateComparisonTile from '@/components/projects/file-management/ProjectFilesWaitingRoomDuplicateComparisonTile.vue'
import { isImageFile, isPdfFile } from '@/helpers/utils/files/filePreview'
import { useSnackbarStore } from '@/store/snackbar'

type DuplicateResolution = 'waiting_room_file' | 'current_file'
type WaitingRoomColumnValue = 1 | 2 | 3 | 4
type WaitingRoomMode = 'selected' | 'all'
type WaitingRoomPdfPreviewStatus = 'queued' | 'rendering' | 'ready' | 'failed'

type WaitingRoomPdfPreviewState = {
  contentKey: string
  sourceUrl: string | null
  status: WaitingRoomPdfPreviewStatus
}

type WaitingRoomPreviewSourceCacheEntry = {
  contentKey: string
  previewUrl: string | null | undefined
  url: string | null | undefined
  downloadUrl: string | null | undefined
  expiresAt: number
}

type DuplicateComparisonConfirmPayload = {
  duplicateResolution: DuplicateResolution
  waitingRoomFileId: string
  rejectWaitingRoomFileIds: string[]
  comparisonMode?: 'file_management' | 'waiting_room_only'
  candidateFileId?: string
}

interface Props {
  modelValue: boolean
  file?: WaitingRoomFile | null
  loading?: boolean
  duplicateLikeOnly?: boolean
  archiveReplacementFileId?: string | null
  waitingRoomOnly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  file: null,
  loading: false,
  duplicateLikeOnly: false,
  archiveReplacementFileId: null,
  waitingRoomOnly: false,
})

interface Emit {
  (event: 'update:modelValue', value: boolean): void
  (event: 'confirm', payload: DuplicateComparisonConfirmPayload): void
  (event: 'cancel'): void
  (event: 'stale'): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

const comparison = ref<WaitingRoomDuplicateComparisonResponse | null>(null)
const comparisonLoading = ref(false)
const matchingFilesLoading = ref(false)
const waitingRoomMode = ref<WaitingRoomMode>('selected')
const waitingRoomSwitching = ref(false)
const activeSwitchRequestId = ref(0)
const initialWaitingRoomFileId = ref<string | null>(null)
const selectedResolution = ref<DuplicateResolution>('waiting_room_file')
const selectedWaitingRoomFileId = ref<string | null>(null)
const waitingRoomColumns = ref<WaitingRoomColumnValue>(3)
const pdfScale = ref(100)
const currentPdfRef = ref<any>(null)
const currentPage = ref(1)
const currentPageCount = ref(1)
const currentLoading = ref(false)
const waitingRoomPageById = ref<Record<string, number>>({})
const waitingRoomPageCountById = ref<Record<string, number>>({})
const waitingRoomLoadingById = ref<Record<string, boolean>>({})
const waitingRoomPdfPreviewStates = ref<Record<string, WaitingRoomPdfPreviewState>>({})
const waitingRoomPreviewSourceCache = ref<Record<string, WaitingRoomPreviewSourceCacheEntry>>({})
const MAX_CONCURRENT_WAITING_ROOM_PDF_RENDERS = 2
const PREVIEW_URL_CACHE_TTL_MS = 14 * 60 * 1000
const dialogProps = {
  fullscreen: true,
  scrollable: true,
}
const currentFileCheckboxProps = {
  label: 'Current File',
  color: 'primary',
  hideDetails: true,
  density: 'compact' as const,
}
const waitingRoomColumnOptions: { value: WaitingRoomColumnValue; label: string }[] = [
  { value: 1, label: 'Show 1 column view' },
  { value: 2, label: 'Show 2 column view' },
  { value: 3, label: 'Show 3 column view' },
  { value: 4, label: 'Show 4 column view' },
]

const defaultWaitingRoomColumnsForCount = (count: number): WaitingRoomColumnValue => {
  if (count <= 1) return 1
  if (count === 2) return 2

  return 3
}
const dragState = ref<{
  target: HTMLElement | null
  left: number
  top: number
  x: number
  y: number
}>({
  target: null,
  left: 0,
  top: 0,
  x: 0,
  y: 0,
})

const currentFile = computed(() => comparison.value?.current_waiting_room_file || comparison.value?.current_main_file || null)
const isWaitingRoomPair = computed(() => comparison.value?.comparison_mode === 'waiting_room_only')
const primaryWaitingRoomFile = computed(() => comparison.value?.waiting_room_file || null)
const showAllFiles = computed(() => waitingRoomMode.value === 'all')
const isWaitingRoomOnlyMode = computed(() => Boolean(props.waitingRoomOnly || (comparison.value && !currentFile.value)))
const matchingWaitingRoomFiles = computed<WaitingRoomDuplicateComparisonFile[]>(() =>
  comparison.value?.waiting_room_files || [],
)
const displayedWaitingRoomFiles = computed<WaitingRoomDuplicateComparisonFile[]>(() => {
  if (showAllFiles.value) return matchingWaitingRoomFiles.value

  return primaryWaitingRoomFile.value ? [primaryWaitingRoomFile.value] : []
})
const waitingRoomFilesCount = computed(() => {
  if (showAllFiles.value) {
    return comparison.value?.waiting_room_files_count ?? matchingWaitingRoomFiles.value.length
  }

  return primaryWaitingRoomFile.value ? 1 : 0
})
const waitingRoomGridStyle = computed<Record<string, string>>(() => ({
  gridTemplateColumns: `repeat(${waitingRoomColumns.value}, minmax(0, 1fr))`,
}))
const waitingRoomGridClass = computed(() => ({
  'waiting-room-duplicate-comparison-files--show-all': true,
  'waiting-room-duplicate-comparison-files--one-column': waitingRoomColumns.value === 1,
  'waiting-room-duplicate-comparison-files--one-column-multiple': waitingRoomColumns.value === 1
    && displayedWaitingRoomFiles.value.length > 1,
  'waiting-room-duplicate-comparison-files--single-file': waitingRoomColumns.value === 1
    && displayedWaitingRoomFiles.value.length === 1,
}))
const showAllButtonText = computed(() => showAllFiles.value ? 'Show Selected File' : 'Show All Files')

const fileUrl = (file: WaitingRoomDuplicateComparisonFile | null) => {
  return file?.preview_url || file?.url || file?.download_url || null
}

const hasPdfPreview = (file: WaitingRoomDuplicateComparisonFile | null) => {
  return Boolean(file && isPdfFile(file.filename) && fileUrl(file))
}

const waitingRoomFileContentKey = (file: WaitingRoomDuplicateComparisonFile) => [
  file.id,
  file.key || '',
  file.etag || '',
  file.last_modified || '',
  file.size || '',
  file.filename,
].join('|')

const waitingRoomFilePreviewSourceUrl = (file: WaitingRoomDuplicateComparisonFile) => fileUrl(file)

const preserveWaitingRoomPreviewSource = (file: WaitingRoomDuplicateComparisonFile) => {
  const contentKey = waitingRoomFileContentKey(file)
  const cached = waitingRoomPreviewSourceCache.value[file.id]
  const now = Date.now()

  if (cached?.contentKey === contentKey && cached.expiresAt > now) {
    return {
      ...file,
      preview_url: cached.previewUrl,
      url: cached.url,
      download_url: cached.downloadUrl,
    }
  }

  waitingRoomPreviewSourceCache.value = {
    ...waitingRoomPreviewSourceCache.value,
    [file.id]: {
      contentKey,
      previewUrl: file.preview_url,
      url: file.url,
      downloadUrl: file.download_url,
      expiresAt: now + PREVIEW_URL_CACHE_TTL_MS,
    },
  }

  return file
}

const normalizeComparisonResponse = (response: WaitingRoomDuplicateComparisonResponse) => ({
  ...response,
  current_main_file: response.current_main_file
    ? preserveWaitingRoomPreviewSource(response.current_main_file)
    : null,
  current_waiting_room_file: response.current_waiting_room_file
    ? preserveWaitingRoomPreviewSource(response.current_waiting_room_file)
    : null,
  waiting_room_file: preserveWaitingRoomPreviewSource(response.waiting_room_file),
  waiting_room_files: response.waiting_room_files?.map(preserveWaitingRoomPreviewSource),
})

const normalizeMatchingWaitingRoomFiles = (files: WaitingRoomDuplicateComparisonFile[]) => (
  files.map(preserveWaitingRoomPreviewSource)
)

const updateCurrentPreviewLoading = (
  file: WaitingRoomDuplicateComparisonFile | null,
  previousFile: WaitingRoomDuplicateComparisonFile | null = null,
  force = false,
) => {
  if (!hasPdfPreview(file)) {
    currentLoading.value = false
    return
  }

  if (force || fileUrl(file) !== fileUrl(previousFile)) {
    currentLoading.value = true
  }
}

const activeInitialWaitingRoomFileId = () => {
  return initialWaitingRoomFileId.value || primaryWaitingRoomFile.value?.id || props.file?.id || null
}

const isWaitingRoomFileSelected = (fileId: string) => {
  return selectedResolution.value === 'waiting_room_file' && selectedWaitingRoomFileId.value === fileId
}

const findKnownWaitingRoomFile = (fileId: string) => {
  const matchingFile = matchingWaitingRoomFiles.value.find(file => file.id === fileId)
  if (matchingFile) return matchingFile

  return primaryWaitingRoomFile.value?.id === fileId ? primaryWaitingRoomFile.value : null
}

const resetPreviewState = () => {
  pdfScale.value = 100
  currentPage.value = 1
  currentPageCount.value = 1
  currentLoading.value = false
  waitingRoomPageById.value = {}
  waitingRoomPageCountById.value = {}
  waitingRoomLoadingById.value = {}
  waitingRoomPdfPreviewStates.value = {}
  waitingRoomPreviewSourceCache.value = {}
}

const resetSelectionState = () => {
  selectedResolution.value = 'waiting_room_file'
  selectedWaitingRoomFileId.value = null
}

const beginWaitingRoomSwitch = () => {
  activeSwitchRequestId.value += 1
  waitingRoomSwitching.value = true
  matchingFilesLoading.value = true

  return activeSwitchRequestId.value
}

const isActiveWaitingRoomSwitch = (requestId: number) => {
  return activeSwitchRequestId.value === requestId
}

const finishWaitingRoomSwitch = (requestId: number) => {
  if (!isActiveWaitingRoomSwitch(requestId)) return

  waitingRoomSwitching.value = false
  matchingFilesLoading.value = false
}

const setupWaitingRoomPreviewState = (
  files: WaitingRoomDuplicateComparisonFile[],
  pruneMissingFiles = false,
) => {
  const pages: Record<string, number> = { ...waitingRoomPageById.value }
  const pageCounts: Record<string, number> = { ...waitingRoomPageCountById.value }
  const loading: Record<string, boolean> = { ...waitingRoomLoadingById.value }
  const pdfStates: Record<string, WaitingRoomPdfPreviewState> = {
    ...waitingRoomPdfPreviewStates.value,
  }

  files.forEach(file => {
    pages[file.id] = pages[file.id] || 1
    pageCounts[file.id] = pageCounts[file.id] || 1
    loading[file.id] = loading[file.id] ?? hasPdfPreview(file)

    if (!hasPdfPreview(file)) return

    const contentKey = waitingRoomFileContentKey(file)
    if (
      pdfStates[file.id]?.contentKey === contentKey
      && pdfStates[file.id]?.sourceUrl === waitingRoomFilePreviewSourceUrl(file)
    ) return

    pages[file.id] = 1
    pageCounts[file.id] = 1
    loading[file.id] = true
    pdfStates[file.id] = {
      contentKey,
      sourceUrl: waitingRoomFilePreviewSourceUrl(file),
      status: showAllFiles.value ? 'queued' : 'rendering',
    }
  })

  if (pruneMissingFiles) {
    const currentFileIds = new Set(files.map(file => file.id))
    Object.keys(pages).forEach(fileId => {
      if (!currentFileIds.has(fileId)) delete pages[fileId]
    })
    Object.keys(pageCounts).forEach(fileId => {
      if (!currentFileIds.has(fileId)) delete pageCounts[fileId]
    })
    Object.keys(loading).forEach(fileId => {
      if (!currentFileIds.has(fileId)) delete loading[fileId]
    })
    Object.keys(pdfStates).forEach(fileId => {
      if (!currentFileIds.has(fileId)) delete pdfStates[fileId]
    })
  }

  waitingRoomPageById.value = pages
  waitingRoomPageCountById.value = pageCounts
  waitingRoomLoadingById.value = loading
  waitingRoomPdfPreviewStates.value = pdfStates
}

const reconcileWaitingRoomPdfPreviewQueue = () => {
  if (!showAllFiles.value) return

  const nextStates: Record<string, WaitingRoomPdfPreviewState> = {
    ...waitingRoomPdfPreviewStates.value,
  }
  const nextLoading: Record<string, boolean> = {
    ...waitingRoomLoadingById.value,
  }

  matchingWaitingRoomFiles.value.forEach(file => {
    if (!hasPdfPreview(file)) return

    const state = nextStates[file.id]
    if (!state || state.status === 'failed') return

    if (
      state.contentKey !== waitingRoomFileContentKey(file)
      || state.sourceUrl !== waitingRoomFilePreviewSourceUrl(file)
    ) {
      nextStates[file.id] = {
        contentKey: waitingRoomFileContentKey(file),
        sourceUrl: waitingRoomFilePreviewSourceUrl(file),
        status: 'queued',
      }
      nextLoading[file.id] = true
    }
  })

  let renderingCount = Object.entries(nextStates).filter(([fileId, state]) => (
    matchingWaitingRoomFiles.value.some(file => file.id === fileId) && state.status === 'rendering'
  )).length
  const queuedFiles = matchingWaitingRoomFiles.value
    .filter(file => hasPdfPreview(file))
    .sort((firstFile, secondFile) => {
      if (firstFile.id === selectedWaitingRoomFileId.value) return -1
      if (secondFile.id === selectedWaitingRoomFileId.value) return 1

      return 0
    })

  queuedFiles.forEach(file => {
    const state = nextStates[file.id]
    if (!state || state.status !== 'queued' || renderingCount >= MAX_CONCURRENT_WAITING_ROOM_PDF_RENDERS) return

    nextStates[file.id] = { ...state, status: 'rendering' }
    nextLoading[file.id] = true
    renderingCount += 1
  })

  waitingRoomPdfPreviewStates.value = nextStates
  waitingRoomLoadingById.value = nextLoading
}

const isWaitingRoomPreviewActive = (file: WaitingRoomDuplicateComparisonFile) => {
  if (!showAllFiles.value) return true
  if (!hasPdfPreview(file)) return true

  const status = waitingRoomPdfPreviewStates.value[file.id]?.status
  return status === 'rendering' || status === 'ready' || status === 'failed'
}

const hasWaitingRoomPreviewFailed = (file: WaitingRoomDuplicateComparisonFile) => (
  waitingRoomPdfPreviewStates.value[file.id]?.status === 'failed'
)

const resetWaitingRoomPreviewStateForFiles = (files: WaitingRoomDuplicateComparisonFile[]) => {
  setupWaitingRoomPreviewState(files, true)
  reconcileWaitingRoomPdfPreviewQueue()
}

const closeDialog = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const loadComparison = async () => {
  if (!props.file?.id) return

  activeSwitchRequestId.value += 1
  const requestId = activeSwitchRequestId.value
  comparisonLoading.value = true
  waitingRoomMode.value = 'selected'
  waitingRoomSwitching.value = false
  matchingFilesLoading.value = false
  comparison.value = null
  initialWaitingRoomFileId.value = null
  resetSelectionState()
  resetPreviewState()

  try {
    const response = await $getWaitingRoomDuplicateComparison(
      props.file.id,
      900,
      false,
      props.duplicateLikeOnly,
      props.archiveReplacementFileId || undefined,
    )
    if (!isActiveWaitingRoomSwitch(requestId)) return

    const normalizedResponse = normalizeComparisonResponse(response.data)
    comparison.value = normalizedResponse
    initialWaitingRoomFileId.value = normalizedResponse.waiting_room_file.id
    selectedWaitingRoomFileId.value = normalizedResponse.waiting_room_file.id
    updateCurrentPreviewLoading(
      normalizedResponse.current_waiting_room_file || normalizedResponse.current_main_file,
      null,
      true,
    )
    setupWaitingRoomPreviewState([normalizedResponse.waiting_room_file])
  } catch (error: any) {
    if (!isActiveWaitingRoomSwitch(requestId)) return

    emit('update:modelValue', false)
    if (error?.response?.status === 409) emit('stale')
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Duplicate Comparison Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to load duplicate comparison.',
    })
  } finally {
    if (isActiveWaitingRoomSwitch(requestId)) {
      comparisonLoading.value = false
    }
  }
}

const toggleShowAllFiles = async () => {
  if (isWaitingRoomPair.value) return
  if (waitingRoomSwitching.value || matchingFilesLoading.value || comparisonLoading.value) return

  if (showAllFiles.value) {
    await switchToSelectedWaitingRoomMode()
    return
  }

  await switchToAllWaitingRoomMode()
}

const switchToSelectedWaitingRoomMode = async () => {
  const initialFileId = activeInitialWaitingRoomFileId()
  if (!initialFileId || !comparison.value) return

  const currentMainFile = comparison.value.current_main_file
  const requestId = beginWaitingRoomSwitch()

  try {
    const response = await $getWaitingRoomDuplicateComparison(
      initialFileId,
      900,
      false,
      props.duplicateLikeOnly,
      props.archiveReplacementFileId || undefined,
    )
    if (!isActiveWaitingRoomSwitch(requestId)) return

    const normalizedResponse = normalizeComparisonResponse(response.data)
    const selectedFile = normalizedResponse.waiting_room_file

    comparison.value = {
      ...normalizedResponse,
      current_main_file: currentMainFile,
      waiting_room_file: selectedFile,
      waiting_room_files: [],
      waiting_room_files_count: 0,
    }
    initialWaitingRoomFileId.value = initialWaitingRoomFileId.value || selectedFile.id
    selectedWaitingRoomFileId.value = selectedFile.id
    selectedResolution.value = 'waiting_room_file'
    waitingRoomMode.value = 'selected'
    setupWaitingRoomPreviewState([selectedFile])
  } catch (error: any) {
    if (!isActiveWaitingRoomSwitch(requestId)) return

    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Selected Waiting Room File Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to load selected Waiting Room file.',
    })
  } finally {
    finishWaitingRoomSwitch(requestId)
  }
}

const switchToAllWaitingRoomMode = async () => {
  const initialFileId = activeInitialWaitingRoomFileId()
  if (!initialFileId || !comparison.value) return

  const previousSelectedWaitingRoomFileId = selectedWaitingRoomFileId.value
  const requestId = beginWaitingRoomSwitch()

  try {
    const matchingResponse = await $getWaitingRoomDuplicateComparisonWaitingRoomFiles(
      initialFileId,
      900,
      props.duplicateLikeOnly,
      props.archiveReplacementFileId || undefined,
    )
    if (!isActiveWaitingRoomSwitch(requestId)) return

    const matchingFiles = normalizeMatchingWaitingRoomFiles(
      matchingResponse.data.waiting_room_files,
    )
    const matchingFilesCount = matchingResponse.data.waiting_room_files_count

    comparison.value = {
      ...comparison.value,
      waiting_room_files: matchingFiles,
      waiting_room_files_count: matchingFilesCount,
    }
    waitingRoomColumns.value = defaultWaitingRoomColumnsForCount(
      matchingFilesCount || matchingFiles.length,
    )
    selectedWaitingRoomFileId.value = matchingFiles.some(file => file.id === previousSelectedWaitingRoomFileId)
      ? previousSelectedWaitingRoomFileId
      : matchingFiles.find(file => file.id === initialWaitingRoomFileId.value)?.id || matchingFiles[0]?.id || null
    waitingRoomMode.value = 'all'
    resetWaitingRoomPreviewStateForFiles(matchingFiles)
  } catch (error: any) {
    if (!isActiveWaitingRoomSwitch(requestId)) return

    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Waiting Room Files Failed',
      text: error?.response?.data?.detail || error?.message || 'Unable to load matching Waiting Room files.',
    })
  } finally {
    finishWaitingRoomSwitch(requestId)
  }
}

const selectCurrentFile = () => {
  if (isWaitingRoomOnlyMode.value) {
    selectedResolution.value = 'waiting_room_file'
    return
  }

  selectedResolution.value = 'current_file'
}

const selectWaitingRoomFile = (fileId: string) => {
  selectedResolution.value = 'waiting_room_file'
  selectedWaitingRoomFileId.value = fileId
  reconcileWaitingRoomPdfPreviewQueue()
}

const updateCurrentPageCount = (pageCount?: number | null) => {
  const nextPageCount = pageCount || currentPdfRef.value?.pageCount || 1
  currentPageCount.value = nextPageCount
  if (currentPage.value > nextPageCount) {
    currentPage.value = nextPageCount
  }
}

const onCurrentPdfLoaded = (document?: { numPages?: number }) => {
  updateCurrentPageCount(document?.numPages)
  currentLoading.value = false
}

const onCurrentPdfRendered = async () => {
  await nextTick()
  updateCurrentPageCount()
  currentLoading.value = false
}

const onCurrentPdfError = () => {
  currentLoading.value = false
}

const waitingRoomPage = (fileId: string) => waitingRoomPageById.value[fileId] || 1
const waitingRoomPageCount = (fileId: string) => waitingRoomPageCountById.value[fileId] || 1
const isWaitingRoomLoading = (fileId: string) => Boolean(waitingRoomLoadingById.value[fileId])

const changeWaitingRoomPage = (fileId: string, direction: -1 | 1) => {
  const file = findKnownWaitingRoomFile(fileId)
  if (!file) return

  const nextPage = waitingRoomPage(fileId) + direction
  if (nextPage < 1 || nextPage > waitingRoomPageCount(fileId)) return

  waitingRoomPageById.value = {
    ...waitingRoomPageById.value,
    [fileId]: nextPage,
  }
  if (isPdfFile(file.filename) && fileUrl(file)) {
    waitingRoomLoadingById.value = {
      ...waitingRoomLoadingById.value,
      [fileId]: true,
    }
  }
}

const setWaitingRoomPdfLoaded = (fileId: string, pageCount?: number | null) => {
  const nextPageCount = pageCount || 1
  waitingRoomPageCountById.value = {
    ...waitingRoomPageCountById.value,
    [fileId]: nextPageCount,
  }
  if (waitingRoomPage(fileId) > nextPageCount) {
    waitingRoomPageById.value = {
      ...waitingRoomPageById.value,
      [fileId]: nextPageCount,
    }
  }
}

const onWaitingRoomPdfLoaded = (fileId: string, pageCount: number) => {
  setWaitingRoomPdfLoaded(fileId, pageCount)
}

const onWaitingRoomPdfRendered = (fileId: string, pageCount: number) => {
  setWaitingRoomPdfLoaded(fileId, pageCount)
  waitingRoomLoadingById.value = {
    ...waitingRoomLoadingById.value,
    [fileId]: false,
  }

  const state = waitingRoomPdfPreviewStates.value[fileId]
  if (!state) return

  if (showAllFiles.value) {
    if (state.status !== 'rendering') return

    waitingRoomPdfPreviewStates.value = {
      ...waitingRoomPdfPreviewStates.value,
      [fileId]: { ...state, status: 'ready' },
    }
    reconcileWaitingRoomPdfPreviewQueue()
    return
  }

  waitingRoomPdfPreviewStates.value = {
    ...waitingRoomPdfPreviewStates.value,
    [fileId]: { ...state, status: 'ready' },
  }
}

const onWaitingRoomPdfError = (fileId: string) => {
  waitingRoomLoadingById.value = {
    ...waitingRoomLoadingById.value,
    [fileId]: false,
  }

  const state = waitingRoomPdfPreviewStates.value[fileId]
  if (!state) return

  waitingRoomPdfPreviewStates.value = {
    ...waitingRoomPdfPreviewStates.value,
    [fileId]: { ...state, status: 'failed' },
  }
  reconcileWaitingRoomPdfPreviewQueue()
}

const matchingWaitingRoomFileIds = () => matchingWaitingRoomFiles.value.map(file => file.id)

const selectedShowAllWaitingRoomFileId = (fileIds: string[], initialFileId: string) => {
  if (selectedWaitingRoomFileId.value && fileIds.includes(selectedWaitingRoomFileId.value)) {
    return selectedWaitingRoomFileId.value
  }

  const fallbackFileId = fileIds.includes(initialFileId) ? initialFileId : fileIds[0]
  selectedWaitingRoomFileId.value = fallbackFileId || null

  return fallbackFileId
}

const confirmSelection = () => {
  const initialFileId = activeInitialWaitingRoomFileId()
  if (!initialFileId) return

  if (isWaitingRoomPair.value) {
    const candidateFileId = comparison.value?.current_waiting_room_file?.id
    if (!candidateFileId) return
    emit('confirm', {
      comparisonMode: 'waiting_room_only',
      candidateFileId,
      duplicateResolution: selectedResolution.value,
      waitingRoomFileId: initialFileId,
      rejectWaitingRoomFileIds: [],
    })
    return
  }

  const duplicateResolution = isWaitingRoomOnlyMode.value
    ? 'waiting_room_file'
    : selectedResolution.value

  if (!showAllFiles.value) {
    emit('confirm', {
      duplicateResolution,
      waitingRoomFileId: initialFileId,
      rejectWaitingRoomFileIds: duplicateResolution === 'current_file' ? [initialFileId] : [],
    })
    return
  }

  const fileIds = matchingWaitingRoomFileIds()
  const selectedFileId = selectedShowAllWaitingRoomFileId(fileIds, initialFileId)
  if (!selectedFileId) return

  const rejectWaitingRoomFileIds = duplicateResolution === 'current_file'
    ? fileIds
    : fileIds.filter(id => id !== selectedFileId)

  emit('confirm', {
    duplicateResolution,
    waitingRoomFileId: selectedFileId,
    rejectWaitingRoomFileIds,
  })
}

const startPreviewPan = (event: MouseEvent) => {
  if (event.button !== 0) return
  if ((event.target as HTMLElement).closest('button, input, textarea, select, a')) return

  const target = event.currentTarget as HTMLElement
  dragState.value = {
    target,
    left: target.scrollLeft,
    top: target.scrollTop,
    x: event.clientX,
    y: event.clientY,
  }
}

const movePreviewPan = (event: MouseEvent) => {
  const state = dragState.value
  if (!state.target) return

  event.preventDefault()
  state.target.scrollLeft = state.left - (event.clientX - state.x)
  state.target.scrollTop = state.top - (event.clientY - state.y)
}

const stopPreviewPan = () => {
  dragState.value.target = null
}

watch(
  () => [props.modelValue, props.file?.id],
  ([isOpen]) => {
    if (isOpen) {
      waitingRoomMode.value = 'selected'
      void loadComparison()
    } else {
      activeSwitchRequestId.value += 1
      comparison.value = null
      comparisonLoading.value = false
      waitingRoomMode.value = 'selected'
      waitingRoomSwitching.value = false
      matchingFilesLoading.value = false
      initialWaitingRoomFileId.value = null
      waitingRoomColumns.value = 3
      resetSelectionState()
      resetPreviewState()
    }
  },
  { immediate: true },
)

watch(currentPage, () => {
  if (currentFile.value && isPdfFile(currentFile.value.filename) && fileUrl(currentFile.value)) {
    currentLoading.value = true
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    v-bind="dialogProps"
    class="waiting-room-duplicate-comparison-dialog"
    @update:model-value="(value: boolean) => emit('update:modelValue', value)"
  >
    <VCard class="waiting-room-duplicate-comparison-card d-flex flex-column">
      <VCardTitle class="waiting-room-duplicate-comparison-title">
        <div class="min-w-0">
          <div class="text-h6 text-truncate">Duplicate Files</div>
          <div class="text-body-2 text-medium-emphasis text-truncate">
            {{ file?.filename || '' }}
          </div>
        </div>
        <div class="waiting-room-duplicate-comparison-title-actions">
          <VBtn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeDialog"
          />
        </div>
      </VCardTitle>

      <VDivider />

      <VCardText class="waiting-room-duplicate-comparison-content">
        <div
          v-if="comparisonLoading"
          class="waiting-room-duplicate-comparison-loader"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="44"
            width="4"
          />
        </div>

        <template v-else-if="comparison">
          <div class="waiting-room-duplicate-comparison-toolbar">
            <VAlert
              type="info"
              variant="tonal"
              density="comfortable"
              class="waiting-room-duplicate-comparison-alert"
            >
              <template v-if="isWaitingRoomOnlyMode">
                Choose <strong>Waiting Room File</strong> to fetch it as the new main version.
              </template>
              <template v-else-if="isWaitingRoomPair">
                Choose <strong>Status Mismatch File</strong> to reject this duplicate, or choose
                <strong>Waiting Room Duplicate</strong> to reject the previous candidate and make this file the new Status Mismatch candidate.
              </template>
              <template v-else>
                Choosing <strong>Current File</strong> keeps the current file and rejects the Waiting Room file(s).<br>
                Choosing <strong>Waiting Room File</strong> follows normal status validation and rejects other the Waiting Room file(s).
              </template>
            </VAlert>
            <VSlider
              v-model="pdfScale"
              :max="500"
              :min="100"
              :step="10"
              label="Scale, %"
              hide-details
              class="waiting-room-duplicate-comparison-scale"
            >
              <template #append>
                <VTextField
                  v-model="pdfScale"
                  type="number"
                  style="width: 80px"
                  density="compact"
                  hide-details
                  variant="outlined"
                  class="pa-1"
                />
              </template>
            </VSlider>
          </div>

          <div
            class="waiting-room-duplicate-comparison-grid"
            :class="{ 'waiting-room-duplicate-comparison-grid--waiting-room-only': isWaitingRoomOnlyMode }"
          >
            <section
              v-if="!isWaitingRoomOnlyMode"
              class="waiting-room-duplicate-comparison-panel"
              :class="{ 'waiting-room-duplicate-comparison-panel--selected': selectedResolution === 'current_file' && !isWaitingRoomOnlyMode }"
            >
              <div class="waiting-room-duplicate-comparison-panel-header py-1">
                <VCheckbox
                  :model-value="selectedResolution === 'current_file' && !isWaitingRoomOnlyMode"
                  v-bind="{ ...currentFileCheckboxProps, label: isWaitingRoomPair ? 'Status Mismatch File' : 'Current File' }"
                  @update:model-value="selectCurrentFile"
                />
              </div>

              <div
                class="waiting-room-duplicate-comparison-preview"
                @mousedown="startPreviewPan"
                @mousemove="movePreviewPan"
                @mouseup="stopPreviewPan"
                @mouseleave="stopPreviewPan"
              >
                <div
                  v-if="currentLoading"
                  class="waiting-room-duplicate-comparison-preview-loader"
                >
                  <VProgressCircular
                    indeterminate
                    color="primary"
                  />
                </div>

                <img
                  v-if="currentFile && isImageFile(currentFile.filename) && fileUrl(currentFile)"
                  :src="fileUrl(currentFile) || undefined"
                  :alt="currentFile.filename"
                  class="waiting-room-duplicate-comparison-image"
                >

                <div
                  v-else-if="currentFile && isPdfFile(currentFile.filename) && fileUrl(currentFile)"
                  class="waiting-room-duplicate-comparison-pdf-container"
                >
                  <div class="waiting-room-duplicate-comparison-pages">
                    <VBtn
                      icon="mdi-arrow-left-bold-circle-outline"
                      size="small"
                      variant="text"
                      :disabled="currentPage <= 1"
                      @click="currentPage--"
                    />
                    <span>{{ currentPage }} / {{ currentPageCount }}</span>
                    <VBtn
                      icon="mdi-arrow-right-bold-circle-outline"
                      size="small"
                      variant="text"
                      :disabled="currentPage >= currentPageCount"
                      @click="currentPage++"
                    />
                  </div>
                  <div
                    class="waiting-room-duplicate-comparison-pdf-wrapper"
                    :style="{ width: `${pdfScale}%` }"
                  >
                    <VuePdfEmbed
                      ref="currentPdfRef"
                      :source="fileUrl(currentFile)"
                      :page="currentPage"
                      @loaded="onCurrentPdfLoaded"
                      @rendered="onCurrentPdfRendered"
                      @loading-failed="onCurrentPdfError"
                      @rendering-failed="onCurrentPdfError"
                    />
                  </div>
                </div>

                <iframe
                  v-else-if="currentFile && fileUrl(currentFile)"
                  :src="fileUrl(currentFile) || undefined"
                  :title="currentFile.filename"
                  class="waiting-room-duplicate-comparison-iframe"
                />

                <div
                  v-else-if="currentFile"
                  class="waiting-room-duplicate-comparison-empty"
                >
                  Preview is not available.
                </div>

                <div
                  v-else
                  class="waiting-room-duplicate-comparison-empty"
                >
                  Current file is not available.
                </div>
              </div>
            </section>

            <section class="waiting-room-duplicate-comparison-panel waiting-room-duplicate-comparison-panel--waiting-room">
              <div class="waiting-room-duplicate-comparison-panel-header waiting-room-duplicate-comparison-files-header">
                <div class="waiting-room-duplicate-comparison-files-title">
                  <span class="font-weight-medium">
                    {{ isWaitingRoomPair ? 'Waiting Room Duplicate' : showAllFiles ? `All Waiting Room Files: ${waitingRoomFilesCount}` : 'Waiting Room File' }}
                  </span>
                </div>
                <div class="waiting-room-duplicate-comparison-files-actions">
                  <div
                    v-if="showAllFiles"
                    class="waiting-room-duplicate-comparison-columns"
                  >
                    <VBtn
                      v-for="columnOption in waitingRoomColumnOptions"
                      :key="columnOption.value"
                      size="x-small"
                      class="waiting-room-duplicate-comparison-columns-btn"
                      :variant="waitingRoomColumns === columnOption.value ? 'flat' : 'outlined'"
                      :color="waitingRoomColumns === columnOption.value ? 'primary' : undefined"
                      :aria-label="columnOption.label"
                      @click="waitingRoomColumns = columnOption.value"
                    >
                      <span
                        v-if="columnOption.value === 4"
                        class="
                          waiting-room-duplicate-comparison-columns-icon
                          waiting-room-duplicate-comparison-columns-icon--four
                        "
                      >
                        <VIcon icon="tabler-columns-2" size="15" />
                        <VIcon icon="tabler-columns-2" size="15" />
                      </span>
                      <VIcon
                        v-else
                        :icon="`tabler-columns-${columnOption.value}`"
                      />
                      <VMenu
                        activator="parent"
                        location="top center"
                        open-on-hover
                        open-delay="100"
                        close-delay="0"
                        offset="1"
                        class="default-tooltip"
                      >
                        <span class="px-2">
                          {{ columnOption.label }}
                        </span>
                      </VMenu>
                    </VBtn>
                  </div>
                  <VBtn
                    v-if="!isWaitingRoomPair"
                    variant="tonal"
                    size=""
                    class="px-2 py-1"
                    :loading="waitingRoomSwitching || matchingFilesLoading"
                    :disabled="comparisonLoading || waitingRoomSwitching || matchingFilesLoading || !comparison"
                    @click="toggleShowAllFiles"
                  >
                    {{ showAllButtonText }}
                  </VBtn>
                </div>
              </div>

              <div
                class="waiting-room-duplicate-comparison-files"
                :class="showAllFiles ? waitingRoomGridClass : 'waiting-room-duplicate-comparison-files--selected'"
                :style="showAllFiles ? waitingRoomGridStyle : undefined"
              >
                <div
                  v-if="!displayedWaitingRoomFiles.length"
                  class="waiting-room-duplicate-comparison-empty"
                >
                  {{ showAllFiles ? 'No matching Waiting Room files.' : 'Waiting Room file is not available.' }}
                </div>
                <ProjectFilesWaitingRoomDuplicateComparisonTile
                  v-for="waitingFile in displayedWaitingRoomFiles"
                  :key="waitingFile.id"
                  :file="waitingFile"
                  :selected="isWaitingRoomFileSelected(waitingFile.id)"
                  :scale="pdfScale"
                  :page="waitingRoomPage(waitingFile.id)"
                  :page-count="waitingRoomPageCount(waitingFile.id)"
                  :loading="isWaitingRoomLoading(waitingFile.id)"
                  :preview-active="isWaitingRoomPreviewActive(waitingFile)"
                  :preview-failed="hasWaitingRoomPreviewFailed(waitingFile)"
                  @select="selectWaitingRoomFile"
                  @page="changeWaitingRoomPage"
                  @loaded="onWaitingRoomPdfLoaded"
                  @rendered="onWaitingRoomPdfRendered"
                  @pdf-error="onWaitingRoomPdfError"
                />
                <div
                  v-if="waitingRoomSwitching"
                  class="waiting-room-duplicate-comparison-files-refresh-overlay"
                >
                  <VProgressCircular
                    indeterminate
                    color="primary"
                    size="32"
                  />
                  <span>Refreshing Waiting Room files…</span>
                </div>
              </div>
            </section>
          </div>
        </template>
      </VCardText>

      <VDivider />

      <VCardActions class="justify-end px-4 py-3">
        <VBtn
          variant="text"
          :disabled="loading"
          @click="closeDialog"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          variant="flat"
          :loading="loading"
          :disabled="comparisonLoading || waitingRoomSwitching || matchingFilesLoading || !comparison"
          @click="confirmSelection"
        >
          Approve
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.waiting-room-duplicate-comparison-card {
  block-size: 100vh;
  overflow: hidden;
}

.waiting-room-duplicate-comparison-title,
.waiting-room-duplicate-comparison-title-actions {
  align-items: center;
  display: flex;
}

.waiting-room-duplicate-comparison-title {
  justify-content: space-between;
}

.waiting-room-duplicate-comparison-title-actions {
  flex: 0 0 auto;
  gap: 8px;
}

.waiting-room-duplicate-comparison-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-block-size: 0;
  padding: 16px;
}

.waiting-room-duplicate-comparison-loader,
.waiting-room-duplicate-comparison-preview-loader,
.waiting-room-duplicate-comparison-empty {
  align-items: center;
  display: flex;
  block-size: 100%;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  justify-content: center;
}

.waiting-room-duplicate-comparison-preview-loader {
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 2;
}

.waiting-room-duplicate-comparison-toolbar {
  align-items: center;
  display: grid;
  flex: 0 0 auto;
  gap: 16px;
  grid-template-columns: minmax(180px, 1fr) minmax(260px, 420px);
  margin-block-end: 14px;
}

.waiting-room-duplicate-comparison-alert {
  max-inline-size: 770px;
  min-inline-size: 0;
}

.waiting-room-duplicate-comparison-grid {
  display: grid;
  flex: 1 1 auto;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-block-size: 0;
}

.waiting-room-duplicate-comparison-grid--waiting-room-only {
  grid-template-columns: minmax(0, 1fr);
}

.waiting-room-duplicate-comparison-panel {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-block-size: 0;
  overflow: hidden;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.waiting-room-duplicate-comparison-panel--selected {
  background: rgba(var(--v-theme-primary), 0.1);
  border-color: rgba(var(--v-theme-primary), 0.55);
}

.waiting-room-duplicate-comparison-panel-header {
  align-items: center;
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  display: grid;
  flex: 0 0 auto;
  gap: 8px;
  grid-template-columns: auto minmax(0, 1fr);
  padding: 6px 12px;
}

.waiting-room-duplicate-comparison-files-header {
  grid-template-columns: minmax(0, 1fr) auto;
}

.waiting-room-duplicate-comparison-files-title {
  display: flex;
  flex-direction: column;
  min-inline-size: 0;
}

.waiting-room-duplicate-comparison-files-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.waiting-room-duplicate-comparison-columns {
  display: flex;
  gap: 4px;
}

.waiting-room-duplicate-comparison-columns-btn {
  border-radius: 5px!important;

  .waiting-room-duplicate-comparison-columns-icon {
    align-items: center;
    display: inline-flex;
  }

  .waiting-room-duplicate-comparison-columns-icon--four {
    inline-size: 24px;
    justify-content: center;

    :deep(.v-icon) {
      font-size: 18px;
    }

    :deep(.v-icon + .v-icon) {
      margin-inline-start: -4px;
    }
  }
}

.waiting-room-duplicate-comparison-files {
  display: grid;
  flex: 1 1 auto;
  gap: 10px;
  min-block-size: 0;
  overflow: auto;
  padding: 10px;
  position: relative;
}

.waiting-room-duplicate-comparison-files-refresh-overlay {
  align-items: center;
  backdrop-filter: blur(1px);
  background: rgba(var(--v-theme-surface), 0.7);
  display: flex;
  flex-direction: column;
  gap: 8px;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 3;
}

.waiting-room-duplicate-comparison-files--one-column {
  align-content: start;
}

.waiting-room-duplicate-comparison-files--single-file {
  block-size: 100%;
}

.waiting-room-duplicate-comparison-files--single-file :deep(.waiting-room-duplicate-comparison-file-tile) {
  min-block-size: 100%;
}

.waiting-room-duplicate-comparison-files--selected :deep(.waiting-room-duplicate-comparison-file-tile) {
  min-block-size: 100%;
}

.waiting-room-duplicate-comparison-files--one-column-multiple :deep(.waiting-room-duplicate-comparison-file-tile) {
  min-block-size: calc(100vh - 286px);
}

.waiting-room-duplicate-comparison-preview {
  cursor: grab;
  flex: 1 1 auto;
  min-block-size: 0;
  overflow: auto;
  padding: 10px;
  position: relative;
}

.waiting-room-duplicate-comparison-image {
  block-size: auto;
  display: block;
  inline-size: 100%;
  margin: 0 auto;
  max-inline-size: 100%;
  object-fit: contain;
}

.waiting-room-duplicate-comparison-pdf-container {
  min-block-size: 100%;
}

.waiting-room-duplicate-comparison-pages {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  min-block-size: 36px;
}

.waiting-room-duplicate-comparison-pdf-wrapper {
  margin: 0 auto;
  min-inline-size: 100%;
  overflow: hidden;
}

.waiting-room-duplicate-comparison-iframe {
  border: 0;
  block-size: 100%;
  inline-size: 100%;
  min-block-size: 420px;
}

@media (max-width: 960px) {
  .waiting-room-duplicate-comparison-toolbar,
  .waiting-room-duplicate-comparison-grid {
    grid-template-columns: 1fr;
  }

  .waiting-room-duplicate-comparison-panel {
    min-block-size: 520px;
  }

  .waiting-room-duplicate-comparison-files {
    grid-template-columns: 1fr !important;
  }
}
</style>
